import React, { useEffect, useState } from "react";
import { ActionHandlerFunction, OrgInfo, DataUsageTotals } from "@preveil-api";
import {
  Account, useAppDispatch, useGetOrgDataUsageQuery, DashboardChartTotalCells, DashboardChartUsedCells, DashboardUsage, ChartsData, collectionApi, AdminErrorMessages, MessageHandlerDisplayType,
  AdminUIActionTypes, LOAD_CHARTS_RETRY_MAX
} from "src/common";
import { Loading } from "src/components";
import { DashboardChart, ErrorPanel } from "./";
import { adminActions } from "src/store";

type AllProps = {
    current_account: Account;
    org_info: OrgInfo;
    handleAction: ActionHandlerFunction;
};

function DashboardChartsComponent(props: AllProps) {
    const { current_account, org_info, handleAction } = props;
    const dispatch = useAppDispatch();
    const [usage_data, setUsageData] = useState<ChartsData | undefined>();
    const [total, setTotal] = useState<number>(0);
    const [used, setUsed] = useState<number>(0);
    const [retry, setRetry] = useState<number>(1);
    const { data, error, isLoading } = useGetOrgDataUsageQuery({
        account_ids: Account.getAccountIdentifiers(current_account),
        body: {
            entity_id: org_info.org_id
        }
    });

    // Description: Ensures cursor is in input field when "Rename" option is selected in row dropdown
    useEffect(() => {
        !!data && handleUsageData(data);
    }, [data]);

    // Description: Calculate data and render charts
    function handleUsageData(usage: DataUsageTotals) {
        const dashboard = new DashboardUsage(usage);
        setUsageData(dashboard.cumulative_data);
        setUsed(dashboard.used);
        setTotal(dashboard.available);

        dispatch(adminActions.setLicenses(usage.user_count));
        dispatch(adminActions.setDashboardUsage({
            used: dashboard.used,
            available: dashboard.available,
            shared: dashboard.shared,
            private: dashboard.private
        }));
    }

    return isLoading ? <Loading className="in-place" /> :
        !!error ? <ErrorPanel
            enable_retry={(retry >= 0)}
            handleAction={() => {
                if (retry < LOAD_CHARTS_RETRY_MAX) {
                    dispatch(collectionApi.util.invalidateTags(["org_data_usage"]));
                    setRetry(Number(retry + 1));
                } else {
                    handleAction({
                        actionType: AdminUIActionTypes.PageErrorMessage,
                        params: {
                            message: AdminErrorMessages.error_charts_retries_expired,
                            display_type: MessageHandlerDisplayType.toastr
                        }
                    });
                    setRetry(-1); // NOTE: This will remove the button on the Error Panel
                }
            }} /> :
            <>
                <div className="license">
                    <h5>Licenses Used: <b>{!!data ? `${data.user_count}` : "1"}</b></h5>
                    <a href={`mailto:customersuccess@preveil.com?subject=Buy%20more%20licenses`}
                        data-tooltip-id="pv-tooltip" data-tooltip-content="Send us an email to buy more licenses"
                        target="_top" className="license-link">Buy More Licenses</a>
                </div>
                <div className="recharts-charts-wrapper">
                    {/* All Storage */}
                    <DashboardChart
                        title={DashboardChartTotalCells.title}
                        data={usage_data?.totals}
                        cells={DashboardChartTotalCells.data}
                        total={total} />
                    {/* Used Storage */}
                    {
                        (!!usage_data && used > 0) &&
                        <DashboardChart
                            title={DashboardChartUsedCells.title}
                            data={usage_data?.used}
                            cells={DashboardChartUsedCells.data} />
                    }
                </div>
            </>;
}

export default React.memo(DashboardChartsComponent);
